import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { analyticsService } from '../services/analyticsService'

// Hook for automatic page view tracking
export function usePageTracking() {
  const location = useLocation()

  useEffect(() => {
    // Track page view on route change
    const pageData = {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname + location.search,
    }

    analyticsService.trackPageView(pageData)
  }, [location])
}

// Hook for tracking user interactions
export function useAnalytics() {
  const trackEvent = (action: string, category: string, label?: string, value?: number) => {
    analyticsService.trackUserInteraction(action, category, label, value)
  }

  const trackSearch = (query: string, resultsCount?: number) => {
    analyticsService.trackSearch(query, resultsCount)
  }

  const trackBusinessView = (businessId: string, businessName: string) => {
    analyticsService.trackBusinessView(businessId, businessName)
  }

  const trackAdoptionView = (adoptionId: string, petName: string) => {
    analyticsService.trackAdoptionView(adoptionId, petName)
  }

  const trackFormSubmission = (formType: string, success: boolean = true) => {
    analyticsService.trackFormSubmission(formType, success)
  }

  const trackRegistration = (userType: 'consumer' | 'business', success: boolean = true) => {
    analyticsService.trackRegistration(userType, success)
  }

  const trackBusinessListingCreation = (success: boolean = true) => {
    analyticsService.trackBusinessListingCreation(success)
  }

  const trackContactForm = (contactType: string, success: boolean = true) => {
    analyticsService.trackContactForm(contactType, success)
  }

  const trackButtonClick = (buttonName: string, location: string) => {
    analyticsService.trackButtonClick(buttonName, location)
  }

  const trackNavigation = (from: string, to: string) => {
    analyticsService.trackNavigation(from, to)
  }

  const trackError = (errorType: string) => {
    analyticsService.trackError(errorType)
  }

  const trackEngagement = (action: string, details?: string) => {
    analyticsService.trackEngagement(action, details)
  }

  const setUserId = (userId: string) => {
    analyticsService.setUserId(userId)
  }

  const setUserProperties = (properties: Record<string, any>) => {
    analyticsService.setUserProperties(properties)
  }

  return {
    trackEvent,
    trackSearch,
    trackBusinessView,
    trackAdoptionView,
    trackFormSubmission,
    trackRegistration,
    trackBusinessListingCreation,
    trackContactForm,
    trackButtonClick,
    trackNavigation,
    trackError,
    trackEngagement,
    setUserId,
    setUserProperties,
  }
}

// Hook for tracking form interactions
export function useFormAnalytics(formType: string) {
  const { trackFormSubmission, trackButtonClick } = useAnalytics()

  const trackFormStart = () => {
    trackButtonClick('form_start', formType)
  }

  const trackFormComplete = (success: boolean = true) => {
    trackFormSubmission(formType, success)
  }

  const trackFormField = (fieldName: string, action: 'focus' | 'blur' | 'change') => {
    trackButtonClick(`form_field_${action}`, `${formType}_${fieldName}`)
  }

  const trackFormValidation = (fieldName: string, isValid: boolean) => {
    trackButtonClick('form_validation', `${formType}_${fieldName}_${isValid ? 'valid' : 'invalid'}`)
  }

  return {
    trackFormStart,
    trackFormComplete,
    trackFormField,
    trackFormValidation,
  }
}

// Hook for tracking business interactions
export function useBusinessAnalytics() {
  const { trackBusinessView, trackBusinessListingCreation, trackButtonClick } = useAnalytics()

  const trackBusinessProfileView = (businessId: string, businessName: string) => {
    trackBusinessView(businessId, businessName)
  }

  const trackBusinessListingView = (listingId: string) => {
    trackButtonClick('view_listing', `business_${listingId}`)
  }

  const trackBusinessListingCreate = (success: boolean = true) => {
    trackBusinessListingCreation(success)
  }

  const trackBusinessContact = (businessId: string, contactMethod: string) => {
    trackButtonClick('contact_business', `${businessId}_${contactMethod}`)
  }

  const trackBusinessFavorite = (businessId: string, isFavorited: boolean) => {
    trackButtonClick('favorite_business', `${businessId}_${isFavorited ? 'add' : 'remove'}`)
  }

  return {
    trackBusinessProfileView,
    trackBusinessListingView,
    trackBusinessListingCreate,
    trackBusinessContact,
    trackBusinessFavorite,
  }
}

// Hook for tracking adoption interactions
export function useAdoptionAnalytics() {
  const { trackAdoptionView, trackButtonClick, trackContactForm } = useAnalytics()

  const trackAdoptionListingView = (adoptionId: string, petName: string) => {
    trackAdoptionView(adoptionId, petName)
  }

  const trackAdoptionContact = (_adoptionId: string, contactMethod: string) => {
    trackContactForm(`adoption_${contactMethod}`, true)
  }

  const trackAdoptionFavorite = (adoptionId: string, isFavorited: boolean) => {
    trackButtonClick('favorite_adoption', `${adoptionId}_${isFavorited ? 'add' : 'remove'}`)
  }

  const trackAdoptionShare = (adoptionId: string, shareMethod: string) => {
    trackButtonClick('share_adoption', `${adoptionId}_${shareMethod}`)
  }

  return {
    trackAdoptionListingView,
    trackAdoptionContact,
    trackAdoptionFavorite,
    trackAdoptionShare,
  }
}
